import React, { useState } from 'react';
import styles from './styles.module.scss';

export type ConsentAnswer = boolean | null;

export interface OptionalConsentAnswers {
  enpalMarketingConsent: ConsentAnswer;
  thirdPartyMarketing: ConsentAnswer;
  thirdPartyProfiling: ConsentAnswer;
}

export const DEFAULT_OPTIONAL_CONSENTS: OptionalConsentAnswers = {
  enpalMarketingConsent: null,
  thirdPartyMarketing: null,
  thirdPartyProfiling: null,
};

export function countAnsweredConsents(answers: OptionalConsentAnswers): number {
  return (Object.values(answers) as ConsentAnswer[]).filter((v) => v !== null).length;
}

export function areAllConsentsAnswered(answers: OptionalConsentAnswers): boolean {
  return countAnsweredConsents(answers) === 3;
}

interface OptionalConsentsProps {
  answers: OptionalConsentAnswers;
  onChange: (answers: OptionalConsentAnswers) => void;
}

type ConsentKey = keyof OptionalConsentAnswers;

const CONSENT_ITEMS: Array<{
  key: ConsentKey;
  title: string;
  description: React.ReactNode;
  note?: string;
}> = [
  {
    key: 'enpalMarketingConsent',
    title: 'Comunicazioni commerciali Enpal',
    description:
      'Acconsento a ricevere da Enpal Italia S.r.l. offerte e novità su fotovoltaico, batterie e pompe di calore, via email, SMS, WhatsApp e telefono.',
    note: 'Serve solo per i contatti successivi: senza questo consenso ti chiamiamo comunque per la richiesta che hai appena fatto.',
  },
  {
    key: 'thirdPartyMarketing',
    title: 'Comunicazione dei dati a società partner',
    description: (
      <>
        Acconsento a comunicare i miei dati alle società partner indicate{' '}
        <a
          className={styles.partnerLink}
          href="https://www.enpal.com/it/protezione-dei-dati"
          target="_blank"
          rel="noopener noreferrer"
        >
          nell&apos;elenco
        </a>
        , che potranno contattarmi con proprie offerte nei settori energia e
        utilities, servizi finanziari, edilizia e ristrutturazioni. Agiscono come
        titolari autonomi.
      </>
    ),
  },
  {
    key: 'thirdPartyProfiling',
    title: 'Profilazione per proposte su misura',
    description:
      "Acconsento a che Enpal Italia analizzi i miei consumi, le caratteristiche dell'immobile e le mie interazioni con i suoi canali per propormi la soluzione più adatta. Nessuna decisione automatizzata.",
  },
];

/**
 * Blocco consensi facoltativi — compresso / espanso (mockup phone slide)
 */
const OptionalConsents: React.FC<OptionalConsentsProps> = ({ answers, onChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const answeredCount = countAnsweredConsents(answers);

  const setAll = (value: boolean) => {
    onChange({
      enpalMarketingConsent: value,
      thirdPartyMarketing: value,
      thirdPartyProfiling: value,
    });
    setIsExpanded(true);
  };

  const setOne = (key: ConsentKey, value: boolean) => {
    onChange({ ...answers, [key]: value });
  };

  return (
    <div className={styles.consentCard}>
      <h4 className={styles.title}>Consensi facoltativi</h4>
      <p className={styles.intro}>
        Facoltativi: puoi rispondere oppure lasciare vuoto. Se non rispondi,
        verranno registrati come « No ». Il preventivo lo ricevi comunque.
      </p>

      <div className={styles.toolbar}>
        <div className={styles.bulkActions}>
          <button type="button" className={styles.bulkBtn} onClick={() => setAll(true)}>
            Sì a tutte
          </button>
          <button type="button" className={styles.bulkBtn} onClick={() => setAll(false)}>
            No a tutte
          </button>
        </div>
        <button
          type="button"
          className={styles.toggleLink}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          {isExpanded
            ? 'Nascondi le domande'
            : `Mostra le 3 domande · ${answeredCount} di 3`}
        </button>
      </div>

      {isExpanded && (
        <div className={styles.questions}>
          {CONSENT_ITEMS.map((item) => (
            <div key={item.key} className={styles.question}>
              <h5 className={styles.questionTitle}>{item.title}</h5>
              <p className={styles.questionDescription}>{item.description}</p>
              {item.note && <p className={styles.questionNote}>{item.note}</p>}
              <div className={styles.choiceRow}>
                <button
                  type="button"
                  className={`${styles.choiceBtn} ${
                    answers[item.key] === true ? styles.selected : ''
                  }`}
                  onClick={() => setOne(item.key, true)}
                  aria-pressed={answers[item.key] === true}
                >
                  <span className={styles.radioDot} />
                  Sì, acconsento
                </button>
                <button
                  type="button"
                  className={`${styles.choiceBtn} ${
                    answers[item.key] === false ? styles.selected : ''
                  }`}
                  onClick={() => setOne(item.key, false)}
                  aria-pressed={answers[item.key] === false}
                >
                  <span className={styles.radioDot} />
                  No, grazie
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OptionalConsents;
